/**
 * `SecretShownOnce` — ADR-0162 §3 Slice 4 reusable "copy this now" panel.
 *
 * Mirrors the `SnippetBlock` copy pattern in `SettingsEndpoint.tsx`
 * (~lines 315-369) and the site-key rotate "shown-once" framing: a mono
 * secret block with an inline COPY / COPIED button (Copy → Check icon
 * swap, 1800ms reset) plus a coral warning banner telling the merchant
 * this is the ONLY time the secret is retrievable.
 *
 * Used by:
 *   - `CustomSkillModal` (webhook create — secret returned once)
 *   - `SettingsSkills` (rotate secret — same shown-once contract)
 */

import { Check, Copy } from 'lucide-react';
import { useState } from 'react';

import { useC } from '../../lib/theme.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

export interface SecretShownOnceProps {
  secret: string;
  /** Optional accessible label for the copy button. Defaults to
   *  "Copy secret". */
  copyLabel?: string;
  /** Optional one-line explanation of what the secret is for. Defaults to
   *  the signing-verification purpose. */
  purpose?: string;
  /** Kicker above the block. Defaults to "Signing secret". */
  kicker?: string;
}

const DEFAULT_PURPOSE =
  'Sill signs every request it sends to your endpoint with this secret ' +
  '(an X-Sill-Signature header, HMAC-SHA256). Store it on your endpoint ' +
  'so you can verify each call is genuinely from Sill.';

export function SecretShownOnce({
  secret,
  copyLabel = 'Copy secret',
  purpose = DEFAULT_PURPOSE,
  kicker = 'Signing secret',
}: SecretShownOnceProps): JSX.Element {
  const C = useC();
  const [copied, setCopied] = useState(false);
  const copy = (): void => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      void navigator.clipboard.writeText(secret);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <div className="space-y-3">
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: C.muted,
        }}
      >
        {kicker}
      </div>
      <p
        style={{
          fontFamily: sansFont,
          fontSize: 12,
          color: C.body,
          lineHeight: 1.5,
          margin: 0,
        }}
      >
        {purpose}
      </p>
      <div
        role="alert"
        className="p-3"
        style={{
          background: C.coralDim,
          border: `1px solid rgba(255,107,71,0.3)`,
          borderRadius: 2,
          fontFamily: sansFont,
          fontSize: 12,
          color: C.text,
          lineHeight: 1.5,
        }}
      >
        Copy this now — it is shown only once and cannot be retrieved later.
      </div>
      <div
        className="relative p-3"
        style={{
          background: C.surface2,
          border: `1px solid ${C.border}`,
          borderRadius: 2,
        }}
        data-testid="secret-shown-once"
      >
        <button
          type="button"
          onClick={copy}
          aria-label={copyLabel}
          className="absolute top-2.5 right-2.5 flex items-center gap-1.5 px-2 py-1"
          style={{
            background: C.bg,
            border: `1px solid ${C.border}`,
            fontFamily: monoFont,
            fontSize: 10,
            color: copied ? C.limeFg : C.muted,
            borderRadius: 2,
          }}
        >
          {copied ? <Check size={10} /> : <Copy size={10} />}
          {copied ? 'COPIED' : 'COPY'}
        </button>
        <pre
          style={{
            fontFamily: monoFont,
            fontSize: 12,
            color: C.text,
            lineHeight: 1.6,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
            paddingRight: 72,
            margin: 0,
          }}
        >
          {secret}
        </pre>
      </div>
    </div>
  );
}
